import { SECONDS_PER_HOUR } from '@atlassian/jira-time-tracking-formatter/src/constants.tsx';
import { RecommendationStatus } from './constants.tsx';
import type {
	RecommendationStatusProps,
	RecommendationStatusResult,
	RecommendationStatusType,
} from './types.tsx';

const MINIMUM_UNITS = 0;

type GetTargetBoundsFromAverageProps = {
	average: number;
};

export const getTargetBoundsFromAverage = ({ average }: GetTargetBoundsFromAverageProps) => {
	const targetLower = Math.round(Math.min(Math.max(average - 1, MINIMUM_UNITS), average * 0.9));
	const targetUpper = Math.round(Math.max(average * 1.1, average + 1));
	return { targetLower, targetUpper };
};

type SecondsToHoursProps = {
	seconds: number;
};

export const secondsToHours = ({ seconds }: SecondsToHoursProps) =>
	Math.floor(seconds / SECONDS_PER_HOUR);

export const getRecommendationStatus = ({
	average,
	value,
	estimationUnit,
}: RecommendationStatusProps): RecommendationStatusResult => {
	if (!average) {
		return { recommendationStatus: RecommendationStatus.NO_TARGET, range: null };
	}

	const { targetLower, targetUpper } = getTargetBoundsFromAverage({ average });

	const convertedLower =
		estimationUnit === 'TIME_TRACKING'
			? Math.floor(targetLower / SECONDS_PER_HOUR) * SECONDS_PER_HOUR
			: targetLower;
	const convertedUpper =
		estimationUnit === 'TIME_TRACKING'
			? Math.ceil(targetUpper / SECONDS_PER_HOUR) * SECONDS_PER_HOUR
			: targetUpper;

	let recommendationStatus: RecommendationStatusType;
	if (value < convertedLower) {
		recommendationStatus = RecommendationStatus.BELOW_TARGET;
	} else if (value > convertedUpper) {
		recommendationStatus = RecommendationStatus.ABOVE_TARGET;
	} else {
		recommendationStatus = RecommendationStatus.WITHIN_TARGET;
	}

	return {
		recommendationStatus,
		range: {
			lower: convertedLower,
			upper: convertedUpper,
		},
	};
};

export const textContentMatcher =
	(textContent: string) =>
	(content: string, node: Element | null): boolean => {
		const hasText = (newNode: Element | null): boolean =>
			newNode != null && newNode.textContent === textContent;

		const nodeHasText = hasText(node);
		const childrenDontHaveText =
			!node || Array.from(node.children).every((child) => !hasText(child));

		return nodeHasText && childrenDontHaveText;
	};
